// Message protocol between the admin dashboard's Station Board Studio (the
// parent window) and this app running as an embedded live preview.
//
//   preview -> studio   { source: PREVIEW_SOURCE, type: "ready" }
//   studio  -> preview  { source: STUDIO_SOURCE, type: "render", config, screenId?, still? }
//
// `still` (used by the dashboard's screenshot generator) holds animations
// such as the radar loop on their final frame.
//
// The preview renders whatever config it is sent, including announcement HTML,
// on this app's origin — the same origin real boards sign in on. So configs
// are accepted only from the dashboard's own origins, and the HTML is
// sanitized as well.

export const PREVIEW_SOURCE = "ladder-stationboard-preview";
export const STUDIO_SOURCE = "ladder-station-board-studio";

const DEFAULT_ORIGINS = [
  "https://admin.laddersuite.com",
  "https://ladderemsrn.web.app",
  "https://ladderemsrn.firebaseapp.com",
  "https://ladderdashboard.web.app",
  "https://ladderdashboard.firebaseapp.com",
];

export function allowedOrigins(extra = process.env.REACT_APP_PREVIEW_ORIGINS) {
  const more = String(extra || "")
    .split(",")
    .map((s) => s.trim().replace(/\/+$/, ""))
    .filter(Boolean);
  return [...DEFAULT_ORIGINS, ...more];
}

export function isAllowedOrigin(origin, list = allowedOrigins()) {
  if (!origin || typeof origin !== "string") return false;
  if (list.includes(origin)) return true;
  // Local development of the dashboard (vite on any port).
  return /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);
}

// Returns { config, screenId } for a valid render message, else null.
export function readRenderMessage(event, list) {
  if (!isAllowedOrigin(event?.origin, list)) return null;
  const msg = event.data;
  if (!msg || msg.source !== STUDIO_SOURCE || msg.type !== "render") return null;
  const config = msg.config;
  if (!config || typeof config !== "object" || !Array.isArray(config.screens)) return null;
  return { config, screenId: typeof msg.screenId === "string" ? msg.screenId : null, still: msg.still === true };
}

// Strip anything executable from designer-authored HTML: script-like
// elements, inline event handlers, and javascript:/data: URLs in links.
const BLOCKED_TAGS = ["script", "iframe", "object", "embed", "link", "meta", "style", "base", "form"];

export function sanitizeHtml(html, doc = typeof document !== "undefined" ? document : null) {
  if (!html) return "";
  if (!doc || typeof DOMParser === "undefined") return String(html).replace(/<[^>]*>/g, "");
  const parsed = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  parsed.body.querySelectorAll(BLOCKED_TAGS.join(",")).forEach((el) => el.remove());
  parsed.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || "").trim().toLowerCase();
      if (name.startsWith("on")) el.removeAttribute(attr.name);
      else if (["href", "src", "xlink:href", "action", "formaction"].includes(name) && /^(javascript|data|vbscript):/.test(value)) {
        el.removeAttribute(attr.name);
      }
    });
  });
  return parsed.body.innerHTML;
}

export function sanitizeConfig(config) {
  return {
    ...config,
    screens: (config.screens || []).map((s) => ({
      ...s,
      widgets: (s.widgets || []).map((w) =>
        w.type === "announcements" && w.config?.html
          ? { ...w, config: { ...w.config, html: sanitizeHtml(w.config.html) } }
          : w
      ),
    })),
  };
}
